import { ApolloClient, ApolloProvider, createHttpLink } from '@apollo/client'
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev'
import { setContext } from '@apollo/client/link/context'
import {
  AccountInfo,
  BrowserAuthError,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  InteractionStatus,
  PublicClientApplication,
} from '@azure/msal-browser'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { CircularProgress, CssBaseline, StyledEngineProvider, ThemeProvider } from '@mui/material'
import { Provider, observer } from 'mobx-react'
import React from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import cache from '../../apollo/cache'
import { isConnectedToInternet } from '../../common/utils'
import { msalConfig, tokenRequest } from '../auth/MsalConfig'
import authStore from '../auth/authStore'
import stationStore from '../station/stationStore'
import Footer from './Footer'
import reminderStore from './ReminderStore'
import { RootDiv } from './RootComponents'
import Routes from './Routes'
import UserProvider from './UserProvider'
import mojovaTheme from './theme'

const stores = {
  authStore,
  stationStore,
  reminderStore,
}

const msalInstance = new PublicClientApplication(msalConfig)

const ApolloConnection = observer(() => {
  const { instance, inProgress, accounts } = useMsal()

  const isAuthenticated = useIsAuthenticated()

  if (inProgress !== InteractionStatus.None) {
    return <CircularProgress />
  }

  const activeAccount = instance.getActiveAccount()
  if (!activeAccount && accounts.length > 0) {
    instance.setActiveAccount(accounts[0])
    authStore.setAccount(accounts[0])
  }

  const withToken = setContext(async (_, { headers }) => {
    let accessToken = ''
    await instance.initialize()
    const account = instance.getActiveAccount()
    try {
      if (account) {
        const tokenResponse = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: account as AccountInfo,
          // Skip the hidden iframe fallback, which needs a redirect bridge page in MSAL v5
          cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
        })
        accessToken = tokenResponse.accessToken
      }
    } catch (err) {
      if (isConnectedToInternet()) {
        try {
          if (!(err instanceof InteractionRequiredAuthError)) {
            throw err
          }
          await instance.acquireTokenRedirect({ ...tokenRequest, account: account as AccountInfo })
          // The page is navigating away; never resolve so no request or logout is issued meanwhile
          return await new Promise<never>(() => undefined)
        } catch (redirectErr) {
          if (redirectErr instanceof BrowserAuthError && redirectErr.errorCode === 'interaction_in_progress') {
            return await new Promise<never>(() => undefined)
          }
          console.error(redirectErr)
          authStore.logout()
          stationStore.setActiveStationId(undefined)
          reminderStore.removeAllSnoozedReminders()
          await instance.logoutRedirect({
            account: account,
            postLogoutRedirectUri: process.env.REACT_APP_LOGIN_REDIRECT_URI as string,
          })
        }
      }
    }
    if (isAuthenticated && !accessToken && inProgress === InteractionStatus.None && isConnectedToInternet()) {
      const accountAuthStore = authStore.getAccount()
      await instance.logoutRedirect({
        account: accountAuthStore,
        postLogoutRedirectUri: process.env.REACT_APP_LOGIN_REDIRECT_URI as string,
      })
    }
    return {
      headers: {
        ...headers,
        Authorization: accessToken ? `Bearer ${accessToken}` : '',
      },
    }
  })

  const httpLink = createHttpLink({
    uri: `${process.env.REACT_APP_API_URL}/graphql`,
  })

  const link = withToken.concat(httpLink)
  const client = new ApolloClient({
    link,
    cache,
  })

  /**
   * Load all Apollo error message into the bundle to make sure the
   * fallback message linking to https://go.apollo.dev is not triggered.
   * @see https://www.apollographql.com/docs/react/errors/
   */
  loadDevMessages()
  loadErrorMessages()

  return (
    <ApolloProvider client={client}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mojovaTheme}>
          <RootDiv>
            <CssBaseline />
            <UserProvider>
              <Router>
                <Routes />
              </Router>
            </UserProvider>
          </RootDiv>
          <Footer />
        </ThemeProvider>
      </StyledEngineProvider>
    </ApolloProvider>
  )
})

function Root() {
  return (
    <MsalProvider instance={msalInstance}>
      <Provider {...stores}>
        <ApolloConnection />
      </Provider>
    </MsalProvider>
  )
}

export default Root
